/*
 * PipelineDiagram — the real generation pipeline, as a sequence.
 *
 * Every stage name and percentage below is taken from the backend,
 * not written for marketing:
 *   stage names  -> backend/models.py:131  (ProjectStatus)
 *   percentages  -> backend/projects.py    (update_status progress=)
 *
 * These are the same labels the in-app status badge shows, so the
 * marketing page and the product speak one vocabulary.
 *
 * Structure: sequence. Encoding: position along a shared 0-100 rail
 * plus length of the fill. No area, no 3D, no decorative symbols.
 * One DOM tree serves both layouts — a horizontal track from `md`
 * up, a stacked rail below — so nothing is duplicated or hidden
 * from assistive technology.
 */

const STAGES = [
  { n: "01", key: "transcribing", at: 10,  title: "Transcribing", detail: "Whisper turns your voice note or screen recording into text." },
  { n: "02", key: "scripting",    at: 35,  title: "Scripting",    detail: "The transcript is structured into scenes you can edit as text." },
  { n: "03", key: "voicing",      at: 60,  title: "Voicing",      detail: "Narration is generated — or your own recording is kept." },
  { n: "04", key: "rendering",    at: 80,  title: "Rendering",    detail: "Scenes, captions and highlights are transcoded to MP4." },
  { n: "05", key: "manual",       at: 95,  title: "Manual",       detail: "The same script is written out as a printable PDF guide." },
  { n: "06", key: "ready",        at: 100, title: "Ready",        detail: "Download, share a link, or generate the launch kit." },
];

export default function PipelineDiagram() {
  return (
    <div className="border border-rule bg-canvas">
      {/* ---- rail: the shared 0-100 axis ---- */}
      <div className="hidden md:block px-6 pt-6">
        <div className="flex items-baseline justify-between mb-2">
          <span className="eyebrow">Project status</span>
          <span className="font-mono text-xs text-ink-subtle">
            shown mid-run at <span className="text-ink-accent">voicing</span> · 60%
          </span>
        </div>
        <div
          className="relative h-2 bg-surface border border-rule"
          role="img"
          aria-label="Progress axis from 0 to 100 percent, with the six pipeline stages marked at 10, 35, 60, 80, 95 and 100 percent."
        >
          {STAGES.map((s) => (
            <span
              key={s.key}
              className="absolute top-0 bottom-0 w-px bg-rule"
              style={{ left: `${s.at}%` }}
            />
          ))}
          {/* Fill is pinned to a real stage value (voicing = 60%) and
              labelled below, so it reads as a worked example rather
              than an unexplained decorative sweep. */}
          <span className="absolute inset-y-0 left-0 bg-brand" style={{ width: "60%" }} />
        </div>
      </div>

      {/* ---- stages ---- */}
      <ol className="md:grid md:grid-cols-6 md:border-t md:border-rule md:mt-6">
        {STAGES.map((s, i) => (
          <li
            key={s.key}
            className={[
              "relative p-5 min-w-0",
              // mobile: vertical rail down the left
              "border-b border-rule last:border-b-0 pl-12",
              // desktop: columns
              "md:border-b-0 md:border-r md:last:border-r-0 md:pl-5",
              i % 2 === 1 ? "md:bg-surface" : "",
            ].join(" ")}
          >
            {/* connector rail (mobile only) */}
            <span
              aria-hidden="true"
              className="md:hidden absolute left-[1.35rem] top-0 bottom-0 w-px bg-rule"
            />
            <span
              aria-hidden="true"
              className={`md:hidden absolute left-4 top-5 w-3 h-3 border border-rule ${
                s.key === "ready" ? "bg-state-success rounded-full" : "bg-brand"
              }`}
            />

            <div className="flex items-baseline gap-2">
              <span className="font-mono text-2xs font-bold text-ink-subtle tabular-nums">{s.n}</span>
              <span className="font-mono text-2xs text-ink-subtle tabular-nums ml-auto">{s.at}%</span>
            </div>

            <h3 className="font-display font-black text-lg mt-2 tracking-tight">{s.title}</h3>

            {/* the literal status value the app reports */}
            <code className="inline-block mt-1 font-mono text-xs text-ink-accent">{s.key}</code>

            <p className="text-sm text-ink-muted mt-2 leading-snug">{s.detail}</p>
          </li>
        ))}
      </ol>

      <p className="px-5 py-3 border-t border-rule bg-surface font-mono text-xs text-ink-muted">
        A run that fails stops at the stage it reached and reports{" "}
        <code className="text-ink-accent">failed</code> — your render quota is
        refunded automatically.
      </p>
    </div>
  );
}
